import { useState } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Breadcrumbs } from './Breadcrumbs';
import { BreadcrumbTailProvider } from './BreadcrumbTailProvider';
import { TopProgressBar } from './TopProgressBar';
import { PendingLeadsProvider } from '@/contexts/PendingLeadsContext';
import { CountryProvider } from '@/contexts/CountryContext';
import { NotificationsProvider } from '@/contexts/NotificationsContext';
import { CommandPaletteProvider } from '@/components/command-palette';
import { TourProvider } from '@/tour';
import { duration, easing } from '@/lib/motion';

/**
 * La página tal como estaba al montarse su envoltorio animado.
 *
 * Con `<Outlet />` directo, la página que sale (AnimatePresence en modo
 * `wait`) se volvía a pintar con la ruta NUEVA mientras se desvanecía. Si
 * esa ruta monta algo con su propio AnimatePresence —un `Input`, un
 * `ConfirmDialog`— nace dentro de un árbol que ya está saliendo, la salida
 * nunca termina y la pantalla queda en opacidad 0: pasaba al entrar a
 * Regalos › Otorgar desde otra pestaña (2026-10-15). Congelado, lo que sale
 * es la página vieja y la nueva monta sólo cuando termina la salida.
 */
function FrozenOutlet() {
  const outlet = useOutlet();
  const [frozen] = useState(outlet);
  return frozen;
}

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();

  const transition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: duration.base, ease: easing.standard };

  return (
    <CountryProvider>
      {/* Sólo dentro del panel: el contador de solicitudes no tiene sentido
          sin sesión, y pedirlo desde el login daría 401 en bucle. */}
      <PendingLeadsProvider>
        <NotificationsProvider>
          <CommandPaletteProvider>
            {/* Las guías se montan aquí: es el layout que envuelve todas las rutas
          autenticadas y quien provee el país, del que dependen casi todas. */}
            <TourProvider>
              {/* BreadcrumbTailProvider must wrap BOTH <Breadcrumbs /> and the
            page tree rendered by <Outlet />, so any page can push extra
            crumbs via `useBreadcrumbTail()` and have <Breadcrumbs /> pick
            them up from the same context. */}
              <BreadcrumbTailProvider>
                <TopProgressBar />
                <a
                  href="#main-content"
                  className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[9999] focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
                >
                  Ir al contenido principal
                </a>
                <div className="flex h-screen overflow-hidden bg-background">
                  <Sidebar
                    isOpen={sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                  />

                  <div className="flex flex-1 flex-col overflow-hidden">
                    <Header onMenuClick={() => setSidebarOpen(true)} />

                    <main
                      id="main-content"
                      // El `pb-24` que había aquí le reservaba sitio al botón flotante de
                      // la guía. Ya no flota —vive en la cabecera— así que el hueco sobra:
                      // dejaba una banda muerta al final de cada pantalla.
                      className="relative flex-1 overflow-auto bg-background-secondary/60 p-4 lg:p-6"
                    >
                      <div className="mb-4" data-tour="admin.breadcrumbs">
                        <Breadcrumbs />
                      </div>

                      <AnimatePresence mode="wait" initial={false}>
                        <motion.div
                          key={location.pathname}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={transition}
                          className="min-h-full"
                        >
                          <FrozenOutlet />
                        </motion.div>
                      </AnimatePresence>
                    </main>
                  </div>
                </div>
              </BreadcrumbTailProvider>
            </TourProvider>
          </CommandPaletteProvider>
        </NotificationsProvider>
      </PendingLeadsProvider>
    </CountryProvider>
  );
}

export default DashboardLayout;
